import { Logo } from "@/components/Logo";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Onboarding() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    age: "",
    sex: "male",
    height_in: "",
    weight_lb: "",
    goal: "fat_loss",
    experience_level: "beginner",
    injuries: "",
    nutrition_preferences: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile({
        name: form.name,
        age: form.age ? parseInt(form.age) : null,
        sex: form.sex,
        height_in: form.height_in ? parseFloat(form.height_in) : null,
        weight_lb: form.weight_lb ? parseFloat(form.weight_lb) : null,
        goal: form.goal,
        experience_level: form.experience_level,
        injuries: form.injuries,
        nutrition_preferences: form.nutrition_preferences,
      });
      toast.success("Profile saved");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Could not save profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground px-5 py-8">
      <div className="max-w-[480px] mx-auto">
        <Logo className="h-12 w-12 rounded-xl mb-4" />
        <h1 className="font-display text-3xl font-bold">Build your profile</h1>
        <p className="text-muted-foreground mt-1 mb-6">This helps your coach tailor everything to you.</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input data-testid="onb-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-card border-border h-11" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Age</Label>
              <Input data-testid="onb-age" type="number" value={form.age} onChange={(e) => set("age", e.target.value)} className="bg-card border-border h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>Sex</Label>
              <Select value={form.sex} onValueChange={(v) => set("sex", v)}>
                <SelectTrigger data-testid="onb-sex" className="bg-card border-border h-11"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Height (in)</Label>
              <Input data-testid="onb-height" type="number" value={form.height_in} onChange={(e) => set("height_in", e.target.value)} className="bg-card border-border h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>Weight (lb)</Label>
              <Input data-testid="onb-weight" type="number" value={form.weight_lb} onChange={(e) => set("weight_lb", e.target.value)} className="bg-card border-border h-11" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Primary goal</Label>
            <Select value={form.goal} onValueChange={(v) => set("goal", v)}>
              <SelectTrigger data-testid="onb-goal" className="bg-card border-border h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fat_loss">Fat loss</SelectItem>
                <SelectItem value="muscle_gain">Build muscle</SelectItem>
                <SelectItem value="maintain">Maintain / perform</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Experience level</Label>
            <Select value={form.experience_level} onValueChange={(v) => set("experience_level", v)}>
              <SelectTrigger data-testid="onb-exp" className="bg-card border-border h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Injuries / limitations (optional)</Label>
            <Textarea data-testid="onb-injuries" value={form.injuries} onChange={(e) => set("injuries", e.target.value)} className="bg-card border-border" placeholder="e.g. bad left knee" />
          </div>
          <div className="space-y-1.5">
            <Label>Nutrition preferences (optional)</Label>
            <Textarea data-testid="onb-nutrition" value={form.nutrition_preferences} onChange={(e) => set("nutrition_preferences", e.target.value)} className="bg-card border-border" placeholder="e.g. vegetarian, no dairy" />
          </div>
          <Button data-testid="onb-submit" type="submit" disabled={loading} className="w-full h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enter the app"}
          </Button>
        </form>
      </div>
    </div>
  );
}
